import type { Path } from "react-hook-form";

import { localSlugStatus, type SlugStatus } from "../constants/campaign-slug";
import { SLUG_BLOCK_MESSAGES, STEP_BLOCK_MESSAGES } from "../constants/messages";
import {
  configRule,
  datesOrderRule,
  faqItemsRule,
  firstIssue,
  issuePathToString,
  type MechanicRuleValues,
  nameLengthRule,
  prizesRule,
  segmentationFileRule,
  startDatePastRule,
  type RuleIssue,
} from "../validations/mechanic-rules";
import type { CreateGamificationFormData } from "../validations/gamification.validation";
import { hasCoverImage, hasWideBanner } from "../utils/banner-source";

export interface Step {
  number: number;
  title: string;
}

export const WizardStep = {
  BASICS: 1,
  STORES: 2,
  PRIZES: 3,
  CONFIG: 4,
  SEGMENTATION: 5,
  FILES: 6,
  FAQ: 7,
  REVIEW: 8,
} as const;

export const WIZARD_STEPS: Step[] = [
  { number: WizardStep.BASICS, title: "Mecânica e dados" },
  { number: WizardStep.STORES, title: "Lojas" },
  { number: WizardStep.PRIZES, title: "Prêmios" },
  { number: WizardStep.CONFIG, title: "Configuração" },
  { number: WizardStep.SEGMENTATION, title: "Segmentação" },
  { number: WizardStep.FILES, title: "Arquivos" },
  { number: WizardStep.FAQ, title: "FAQ" },
  { number: WizardStep.REVIEW, title: "Revisão" },
];

export const REVIEW_STEP = WizardStep.REVIEW;

type StepFields = Path<CreateGamificationFormData>[];

export function getStepFields(step: number): StepFields {
  const byStep: Record<number, StepFields> = {
    [WizardStep.BASICS]: [
      "campaignType",
      "campaignName",
      "mechanicConfig.presentation.customerFacingName",
      "mechanicConfig.presentation.customerFacingDescription",
      "startDate",
      "endDate",
    ],
    [WizardStep.STORES]: ["stores"],

    [WizardStep.PRIZES]: ["mechanicConfig.prizes"],
    [WizardStep.CONFIG]: ["mechanicConfig", "slug"],
    [WizardStep.SEGMENTATION]: ["segmentationFile"],
    [WizardStep.FILES]: [
      "banner3x4",
      "banner16x9",
      "banner3x4SourceKey",
      "banner16x9SourceKey",
      "regulations",
    ],
    [WizardStep.FAQ]: [],
    [WizardStep.REVIEW]: [],
  };
  return byStep[step] ?? [];
}

export interface StepSemanticError {
  path: Path<CreateGamificationFormData>;
  message: string;
}

type SemanticValues = MechanicRuleValues & {
  stores?: unknown[];
  banner3x4?: unknown;
  banner16x9?: unknown;
  banner3x4SourceKey?: string;
  banner16x9SourceKey?: string;
  slug?: string;
};

function storeFieldsBlock(stores: unknown[] | undefined): string | null {
  for (const store of stores ?? []) {
    const item = store as {
      nickname?: string;
      storeName?: string;
      storeCode?: string;
    };
    if (item.storeCode === "0") continue;
    if (!item.nickname?.trim() || !item.storeName?.trim()) {
      return STEP_BLOCK_MESSAGES.storeFields;
    }
  }
  return null;
}

const toSemanticError = (issue: RuleIssue | null): StepSemanticError | null =>
  issue
    ? {
        path: issuePathToString(issue.path) as Path<CreateGamificationFormData>,
        message: issue.message,
      }
    : null;

export function validateStepSemantics(
  step: number,
  values: SemanticValues,
): StepSemanticError | null {
  const byStep: Record<number, () => RuleIssue | null> = {
    [WizardStep.BASICS]: () =>
      firstIssue(values, [nameLengthRule, datesOrderRule]),
    [WizardStep.PRIZES]: () => prizesRule(values),
    [WizardStep.CONFIG]: () => configRule(values),
    [WizardStep.FAQ]: () => faqItemsRule(values),
  };
  return toSemanticError(byStep[step]?.() ?? null);
}

export interface StepGuardOptions {

  requireCoverImage: boolean;

  requireWideBanner?: boolean;

  slugStatus?: SlugStatus;

  requireSegmentationFile?: boolean;

  blockPastStart?: boolean;
}

function slugBlock(
  values: SemanticValues,
  options: StepGuardOptions,
): string | null {
  const status: SlugStatus = options.slugStatus ?? localSlugStatus(values.slug);
  return SLUG_BLOCK_MESSAGES[status];
}

function bannersBlock(
  values: SemanticValues,
  options: StepGuardOptions,
): string | null {
  if (options.requireCoverImage && !hasCoverImage(values)) {
    return STEP_BLOCK_MESSAGES.banners;
  }
  if (options.requireWideBanner && !hasWideBanner(values)) {
    return STEP_BLOCK_MESSAGES.wideBanner;
  }
  return null;
}

export function getStepBlockReason(
  step: number,
  values: SemanticValues,
  options: StepGuardOptions,
): string | null {
  const requireSegmentationFile = options.requireSegmentationFile ?? true;
  const byStep: Record<number, () => string | null> = {
    [WizardStep.BASICS]: () => {
      if (!values.campaignName?.trim()) return STEP_BLOCK_MESSAGES.campaignName;
      if (!values.startDate || !values.endDate) return STEP_BLOCK_MESSAGES.dates;
      const rules = [nameLengthRule, datesOrderRule];
      if (options.blockPastStart) rules.push(startDatePastRule);
      return firstIssue(values, rules)?.message ?? null;
    },
    [WizardStep.STORES]: () => {
      if ((values.stores ?? []).length === 0) return STEP_BLOCK_MESSAGES.stores;
      return storeFieldsBlock(values.stores);
    },
    [WizardStep.PRIZES]: () => prizesRule(values)?.message ?? null,
    [WizardStep.CONFIG]: () =>
      configRule(values)?.message ?? slugBlock(values, options),
    [WizardStep.SEGMENTATION]: () => {
      if (!requireSegmentationFile) return null;
      return segmentationFileRule(values)?.message ?? null;
    },
    [WizardStep.FILES]: () => bannersBlock(values, options),
    [WizardStep.FAQ]: () => faqItemsRule(values)?.message ?? null,
  };
  return byStep[step]?.() ?? null;
}

export function getPendingIssues(
  values: SemanticValues,
  options: StepGuardOptions = { requireCoverImage: true },
): string[] {
  const issues: string[] = [];
  const push = (message: string | null | undefined) => {
    if (message) issues.push(message);
  };

  if (!values.campaignName?.trim()) push(STEP_BLOCK_MESSAGES.campaignName);
  if (!values.startDate || !values.endDate) push(STEP_BLOCK_MESSAGES.dates);
  push(firstIssue(values, [nameLengthRule, datesOrderRule])?.message);
  if (options.blockPastStart) push(startDatePastRule(values)?.message);
  push(slugBlock(values, options));

  if ((values.stores ?? []).length === 0) push(STEP_BLOCK_MESSAGES.stores);
  else push(storeFieldsBlock(values.stores));

  push(prizesRule(values)?.message);
  push(configRule(values)?.message);

  if (options.requireSegmentationFile ?? true) {
    push(segmentationFileRule(values)?.message);
  }

  if (options.requireCoverImage && !hasCoverImage(values)) {
    push(STEP_BLOCK_MESSAGES.banners);
  }
  if (options.requireWideBanner && !hasWideBanner(values)) {
    push(STEP_BLOCK_MESSAGES.wideBanner);
  }

  push(faqItemsRule(values)?.message);

  return issues;
}
